import { useState, useEffect, useCallback, type JSX } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { Check, Loader2 } from 'lucide-react';
import { API_CONFIG } from '@/lib/api/config';
import { woocommerceApi } from '@/lib/api/services/woocommerce';
import { razorpayApi } from '@/lib/api/services/razorpay';
import { petpoojaApi } from '@/lib/api/services/petpooja';
import { toast } from '@/hooks/use-toast';
import { WhatsAppEmbeddedSignup } from '@/components/WhatsAppEmbeddedSignup';
import { apiClient } from '@/lib/api/client';
import { showApiError } from '@/lib/errors';
import { ApiError } from '@/lib/api';

// ─── Platform Icons ────────────────────────────────────────────────────────────

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8" fill="none">
      <circle cx="16" cy="16" r="16" fill="#25D366" />
      <path d="M22.9 9.1C21.2 7.4 18.9 6.4 16.4 6.4c-5.2 0-9.4 4.2-9.4 9.4 0 1.7.4 3.3 1.2 4.7L7 25.6l5.3-1.4c1.4.7 2.9 1.1 4.4 1.1h.1c5.2 0 9.4-4.2 9.4-9.4-.1-2.5-1.1-4.8-2.7-6.4zm-6.5 14.5c-1.4 0-2.8-.4-4-1.1l-.3-.2-3.1.8.9-3-.2-.3C8.8 18.4 8.4 17 8.4 15.7c0-4.4 3.6-8 8-8 2.1 0 4.1.8 5.6 2.3 1.5 1.5 2.3 3.5 2.3 5.6 0 4.5-3.6 8-7.9 8zm4.4-6c-.2-.1-1.4-.7-1.6-.8-.2-.1-.4-.1-.5.1-.2.2-.6.8-.8 1-.1.2-.3.2-.5.1-.7-.3-1.4-.7-2-1.2-.5-.5-1-1.1-1.4-1.7-.1-.2 0-.4.1-.5.1-.1.2-.3.4-.4.1-.1.2-.3.2-.4.1-.1 0-.3 0-.4-.1-.2-.5-1.3-.7-1.8-.2-.4-.4-.4-.5-.4h-.5c-.2 0-.4.1-.6.3-.5.5-.7 1.1-.7 1.8 0 .7.4 1.4.9 2 1.4 1.8 3.2 3.2 5.3 3.9.3.1.6.2.9.2.6 0 1.2-.3 1.6-.8.2-.3.3-.6.3-1-.1-.2-.3-.3-.4-.4z" fill="white" />
    </svg>
  );
}

function InstagramIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8">
      <defs>
        <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
          <stop offset="0%" stopColor="#fdf497" />
          <stop offset="5%" stopColor="#fdf497" />
          <stop offset="45%" stopColor="#fd5949" />
          <stop offset="60%" stopColor="#d6249f" />
          <stop offset="90%" stopColor="#285AEB" />
        </radialGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill="url(#ig-grad)" />
      <rect x="8" y="8" width="16" height="16" rx="4" stroke="white" strokeWidth="1.5" fill="none" />
      <circle cx="16" cy="16" r="4" stroke="white" strokeWidth="1.5" fill="none" />
      <circle cx="21.5" cy="10.5" r="1" fill="white" />
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8">
      <circle cx="16" cy="16" r="16" fill="#1877F2" />
      <path d="M21 10h-3c-.6 0-1 .4-1 1v2h4l-.5 4H17v10h-4V17h-2v-4h2v-2c0-2.8 1.7-4 4.4-4H21v3z" fill="white" />
    </svg>
  );
}

function LinkedInIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8">
      <rect width="32" height="32" rx="6" fill="#0A66C2" />
      <path d="M10 13h3v10h-3zm1.5-4.5c1 0 1.5.7 1.5 1.5s-.5 1.5-1.5 1.5-1.5-.7-1.5-1.5.5-1.5 1.5-1.5zm4.5 4.5h3v1.4c.5-.9 1.5-1.6 3-1.6 2.8 0 3.5 1.8 3.5 4.2V23h-3v-4.8c0-1-.2-2.2-1.5-2.2-1.4 0-1.5 1-1.5 2.1V23H16V13z" fill="white" />
    </svg>
  );
}

function ShopifyIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8">
      <rect width="32" height="32" rx="6" fill="#96BF48" />
      <path d="M21.5 9.3c0-.1-.1-.2-.2-.2-.1 0-1.8-.1-1.8-.1S18.1 7.7 18 7.6c-.1-.1-.4-.1-.5 0L16.7 8c-.3-.9-.8-1.7-1.7-1.7-.1 0-.1 0-.2 0-.3-.3-.6-.5-1-.5-2.5 0-3.7 3.1-4.1 4.7l-1.8.6c-.5.2-.5.2-.6.7L6.5 22l10.8 2 5.8-1.3L21.5 9.3zM17.2 8.6l-1.1.4c.3-1.1.8-1.6 1.2-1.9.2.4.2.9-.1 1.5zm-2 .7l-2.4.8c.4-1.5 1.2-2.3 1.9-2.5.3.6.4 1.2.5 1.7zm-1-2.6c.1 0 .2 0 .3.1-.7.3-1.4 1-1.7 2.5l-1.8.6c.4-1.5 1.4-3.2 3.2-3.2z" fill="white" />
    </svg>
  );
}

function WooCommerceIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8">
      <rect width="32" height="32" rx="6" fill="#7F54B3" />
      <text x="7" y="22" fill="white" fontSize="16" fontWeight="bold" fontFamily="Arial">W</text>
    </svg>
  );
}

function RazorpayIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8">
      <rect width="32" height="32" rx="6" fill="#3395FF" />
      <path d="M16 7l-5 11h4l-2 7 9-13h-5l3-5H16z" fill="white" />
    </svg>
  );
}

function PetPoojaIcon() {
  return (
    <svg viewBox="0 0 32 32" className="w-8 h-8">
      <rect width="32" height="32" rx="6" fill="#FF6B35" />
      <text x="5" y="22" fill="white" fontSize="13" fontWeight="bold" fontFamily="Arial">PP</text>
    </svg>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface OnboardingSurveyProps {
  onComplete?: () => void;
  onLayoutModeChange?: (mode: 'full' | 'compact') => void;
}

type PlatformId = 'whatsapp' | 'instagram' | 'facebook' | 'linkedin' | 'shopify' | 'woocommerce' | 'razorpay' | 'petpooja';

interface Platform {
  id: PlatformId;
  name: string;
  icon: () => JSX.Element;
  type: 'social' | 'commerce';
  recommended?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const HELP_OPTIONS = [
  { id: 'whatsapp_marketing', label: '💬 WhatsApp Marketing' },
  { id: 'customer_support', label: '🎧 Customer Support' },
  { id: 'sales_automation', label: '⚡ Sales & Automation' },
  { id: 'ecommerce', label: '🛒 E-commerce' },
  { id: 'payments', label: '💳 Payment Collection' },
  { id: 'lead_gen', label: '🎯 Lead Generation' },
  { id: 'broadcast', label: '📣 Broadcast Campaigns' },
  { id: 'chatbot', label: '🤖 Chatbot & AI' },
];

const PLATFORMS: Platform[] = [
  { id: 'whatsapp', name: 'WhatsApp', icon: WhatsAppIcon, type: 'social', recommended: true },
  { id: 'instagram', name: 'Instagram', icon: InstagramIcon, type: 'social', recommended: true },
  { id: 'facebook', name: 'Facebook', icon: FacebookIcon, type: 'social', recommended: true },
  { id: 'linkedin', name: 'LinkedIn', icon: LinkedInIcon, type: 'social', recommended: true },
  { id: 'shopify', name: 'Shopify', icon: ShopifyIcon, type: 'commerce', recommended: true },
  { id: 'woocommerce', name: 'WooCommerce', icon: WooCommerceIcon, type: 'commerce', recommended: true },
  { id: 'razorpay', name: 'Razorpay', icon: RazorpayIcon, type: 'commerce', recommended: true },
  { id: 'petpooja', name: 'PetPooja', icon: PetPoojaIcon, type: 'commerce', recommended: true },
];

// ─── Helper: OAuth popup opener ───────────────────────────────────────────────

function openOAuthPopup(url: string, windowName: string, onDone: () => void) {
  const popup = window.open(url, windowName, 'width=600,height=700,scrollbars=yes,resizable=yes,status=yes,location=yes,toolbar=no,menubar=no');
  if (!popup) {
    window.location.href = url;
    return;
  }
  const timer = setInterval(() => {
    if (popup.closed) {
      clearInterval(timer);
      onDone();
    }
  }, 800);
  const msgHandler = (e: MessageEvent) => {
    if (e.data?.type === 'OAUTH_SUCCESS' || e.data?.success) {
      clearInterval(timer);
      window.removeEventListener('message', msgHandler);
      popup.close();
      onDone();
    }
  };
  window.addEventListener('message', msgHandler);
}

// ─── Social Connect Dialogs ───────────────────────────────────────────────────

function WhatsAppConnectDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <WhatsAppIcon /> Connect WhatsApp Business
          </DialogTitle>
        </DialogHeader>
        <div className="py-2">
          <WhatsAppEmbeddedSignup
            onSuccess={() => { onConnected(); onClose(); }}
            onError={(msg) => showApiError(msg, { fallback: { title: 'WhatsApp connection failed' } })}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

function InstagramConnectDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  const [loading, setLoading] = useState(false);

  const handleConnect = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const origin = window.location.origin;
      const res = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001'}/instagram/oauth/url?origin=${encodeURIComponent(origin)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const authUrl = data.data?.authUrl || data.authUrl;
      const redirectUri = data.data?.redirectUri || data.redirectUri;
      if (redirectUri) localStorage.setItem('instagram_oauth_redirect_uri', redirectUri);
      if (!authUrl) throw ApiError.fromResult(data, 'Instagram OAuth not configured');
      onClose();
      openOAuthPopup(authUrl, 'instagram-oauth', onConnected);
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to start Instagram OAuth' } });
    } finally {
      setLoading(false);
    }
  }, [onClose, onConnected]);

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <InstagramIcon /> Connect Instagram
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500 py-2">
          You'll be redirected to Instagram to authorize WeNext to access your business account.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConnect}
            disabled={loading}
            className="bg-gradient-to-r from-[#fd5949] to-[#d6249f] text-white border-0 hover:opacity-90"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Log in with Instagram
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function FacebookConnectDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  const [loading, setLoading] = useState(false);

  const handleConnect = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
      const res = await fetch(`${baseUrl}/facebook/oauth/url`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!data.authUrl) throw ApiError.fromResult(data, 'Facebook OAuth not configured');
      onClose();
      openOAuthPopup(data.authUrl, 'facebook-oauth', onConnected);
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to start Facebook OAuth' } });
    } finally {
      setLoading(false);
    }
  }, [onClose, onConnected]);

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FacebookIcon /> Connect Facebook
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500 py-2">
          You'll be redirected to Facebook to authorize WeNext to access your business pages and account.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConnect}
            disabled={loading}
            className="bg-[#1877F2] hover:bg-[#0d65d9] text-white border-0"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Log in with Facebook
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function LinkedInConnectDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  const [loading, setLoading] = useState(false);

  const handleConnect = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/linkedin/oauth/url');
      if (response.error || !response.data?.authUrl) throw ApiError.fromResult(response, 'LinkedIn OAuth not configured');
      onClose();
      openOAuthPopup(response.data.authUrl, 'linkedin-oauth', onConnected);
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Failed to start LinkedIn OAuth' } });
    } finally {
      setLoading(false);
    }
  }, [onClose, onConnected]);

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LinkedInIcon /> Connect LinkedIn
          </DialogTitle>
        </DialogHeader>
        <p className="text-sm text-gray-500 py-2">
          You'll be redirected to LinkedIn to authorize WeNext to access your company pages and profile.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleConnect}
            disabled={loading}
            className="bg-[#0A66C2] hover:bg-[#084f97] text-white border-0"
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Log in with LinkedIn
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Commerce Connect Dialogs ─────────────────────────────────────────────────

function ShopifyDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  const [shop, setShop] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    if (!shop.trim() || !accessToken.trim()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await fetch(`${API_CONFIG.BASE_URL}/shopify/manual-install`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ shop: shop.trim(), accessToken: accessToken.trim(), apiVersion: '2025-01' }),
      });
      const json = await res.json();
      if (!res.ok || !json?.ok) throw ApiError.fromResult(json, 'Connection failed');
      toast({ variant: "success", title: 'Shopify connected!' });
      onConnected();
      onClose();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Connection failed' } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShopifyIcon /> Connect Shopify</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Shop URL</Label>
            <Input placeholder="yourstore.myshopify.com" value={shop} onChange={e => setShop(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Access Token</Label>
            <Input type="password" placeholder="shpat_xxxxxxxxxxxxxxxx" value={accessToken} onChange={e => setAccessToken(e.target.value)} />
            <p className="text-xs text-gray-400">Shopify Admin → Apps → Develop apps → API credentials</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConnect} disabled={loading || !shop.trim() || !accessToken.trim()} className="bg-[#96BF48] hover:bg-[#7da33a] text-white">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Connect
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function WooCommerceDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  const [storeUrl, setStoreUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    if (!storeUrl.trim() || !consumerKey.trim() || !consumerSecret.trim()) return;
    setLoading(true);
    try {
      await woocommerceApi.connect({ businessId: '', storeUrl: storeUrl.trim(), consumerKey: consumerKey.trim(), consumerSecret: consumerSecret.trim() });
      toast({ variant: "success", title: 'WooCommerce connected!' });
      onConnected();
      onClose();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Connection failed' } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><WooCommerceIcon /> Connect WooCommerce</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Store URL</Label>
            <Input placeholder="https://yourstore.com" value={storeUrl} onChange={e => setStoreUrl(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Consumer Key</Label>
            <Input type="password" placeholder="ck_xxxxxxxxxxxxxxxx" value={consumerKey} onChange={e => setConsumerKey(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Consumer Secret</Label>
            <Input type="password" placeholder="cs_xxxxxxxxxxxxxxxx" value={consumerSecret} onChange={e => setConsumerSecret(e.target.value)} />
            <p className="text-xs text-gray-400">WooCommerce → Settings → Advanced → REST API</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConnect} disabled={loading || !storeUrl.trim() || !consumerKey.trim() || !consumerSecret.trim()} className="bg-[#7F54B3] hover:bg-[#6a4398] text-white">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Connect
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RazorpayDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  const [keyId, setKeyId] = useState('');
  const [keySecret, setKeySecret] = useState('');
  const [accountName, setAccountName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    if (!keyId.trim() || !keySecret.trim()) return;
    setLoading(true);
    try {
      await razorpayApi.connect({ businessId: '', keyId: keyId.trim(), keySecret: keySecret.trim(), accountName: accountName.trim() || undefined });
      toast({ variant: "success", title: 'Razorpay connected!' });
      onConnected();
      onClose();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Connection failed' } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RazorpayIcon /> Connect Razorpay</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Key ID</Label>
            <Input placeholder="rzp_live_xxxxxxxxxxxxxxxx" value={keyId} onChange={e => setKeyId(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Key Secret</Label>
            <Input type="password" placeholder="••••••••••••••••" value={keySecret} onChange={e => setKeySecret(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Account Name <span className="text-gray-400 font-normal">(optional)</span></Label>
            <Input placeholder="My Business" value={accountName} onChange={e => setAccountName(e.target.value)} />
            <p className="text-xs text-gray-400">Razorpay Dashboard → Settings → API Keys</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConnect} disabled={loading || !keyId.trim() || !keySecret.trim()} className="bg-[#3395FF] hover:bg-[#1a7de0] text-white">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Connect
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PetPoojaDialog({ open, onClose, onConnected }: { open: boolean; onClose: () => void; onConnected: () => void }) {
  const [restaurantId, setRestaurantId] = useState('');
  const [appKey, setAppKey] = useState('');
  const [appSecret, setAppSecret] = useState('');
  const [accessToken, setAccessToken] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConnect = async () => {
    if (!restaurantId.trim() || !appKey.trim() || !appSecret.trim() || !accessToken.trim()) return;
    setLoading(true);
    try {
      await petpoojaApi.connect({ businessAccountId: '', restaurantId: restaurantId.trim(), appKey: appKey.trim(), appSecret: appSecret.trim(), accessToken: accessToken.trim() });
      toast({ variant: "success", title: 'PetPooja connected!' });
      onConnected();
      onClose();
    } catch (e: any) {
      showApiError(e, { fallback: { title: 'Connection failed' } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PetPoojaIcon /> Connect PetPooja</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>Restaurant ID</Label>
            <Input placeholder="Your PetPooja Restaurant ID" value={restaurantId} onChange={e => setRestaurantId(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>App Key</Label>
            <Input placeholder="App Key" value={appKey} onChange={e => setAppKey(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>App Secret</Label>
            <Input type="password" placeholder="App Secret" value={appSecret} onChange={e => setAppSecret(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Access Token</Label>
            <Input type="password" placeholder="Access Token" value={accessToken} onChange={e => setAccessToken(e.target.value)} />
            <p className="text-xs text-gray-400">Available in your PetPooja merchant dashboard under API settings</p>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleConnect} disabled={loading || !restaurantId.trim() || !appKey.trim() || !appSecret.trim() || !accessToken.trim()} className="bg-[#FF6B35] hover:bg-[#e05520] text-white">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Connect
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function OnboardingSurvey({ onComplete, onLayoutModeChange }: OnboardingSurveyProps = {}) {
  const [step, setStep] = useState(0);
  const [websiteOrName, setWebsiteOrName] = useState('');
  const [selectedHelp, setSelectedHelp] = useState<string[]>([]);
  const [connectedPlatforms, setConnectedPlatforms] = useState<Set<PlatformId>>(new Set());
  const [saving, setSaving] = useState(false);
  const [openDialog, setOpenDialog] = useState<PlatformId | null>(null);

  useEffect(() => {
    onLayoutModeChange?.('compact');
    localStorage.removeItem('new_signup');
  }, []);

  const toggleHelp = (id: string) => {
    setSelectedHelp(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const markConnected = (id: PlatformId) => {
    setConnectedPlatforms(prev => new Set([...prev, id]));
    toast({ variant: "success", title: `${PLATFORMS.find(p => p.id === id)?.name} connected!` });
  };

  const saveAndComplete = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await fetch(`${API_CONFIG.BASE_URL}/onboarding/qa`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ website_or_name: websiteOrName, help_topics: selectedHelp, connected_platforms: Array.from(connectedPlatforms) }),
        });
      }
    } catch { /* silent */ } finally {
      setSaving(false);
      onComplete?.();
    }
  };

  // ── Step 0: Website / Name ────────────────────────────────────────────────
  const renderStep0 = () => (
    <div className="flex flex-col items-center justify-center h-full px-8 py-16">
      <div className="w-full max-w-md">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Get started with WeNext</h1>
        <p className="text-gray-500 mb-8">Enter your website or company name and we'll get to work.</p>
        <div className="space-y-6">
          <div>
            <label className="text-sm text-gray-500 mb-1.5 block">Website URL or Company Name</label>
            <Input
              type="text"
              placeholder="e.g. acmecorp.com or Acme Corp"
              value={websiteOrName}
              onChange={e => setWebsiteOrName(e.target.value)}
              className="rounded-xl border-gray-200 h-12 text-base"
              onKeyDown={e => { if (e.key === 'Enter' && websiteOrName.trim()) setStep(1); }}
              autoFocus
            />
          </div>
          <div className="flex justify-end">
            <Button onClick={() => setStep(1)} disabled={!websiteOrName.trim()} className="rounded-xl bg-[#1AB269] hover:bg-[#158f56] text-white px-8 h-11">
              Continue
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // ── Step 1: Help topics ───────────────────────────────────────────────────
  const renderStep1 = () => (
    <div className="flex flex-col items-center justify-center h-full px-8 py-12">
      <div className="w-full max-w-lg">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">What do you need help with?</h1>
        <p className="text-gray-500 mb-8">Select all that apply. This helps us personalise your experience.</p>
        <div className="grid grid-cols-2 gap-3 mb-10">
          {HELP_OPTIONS.map(opt => {
            const selected = selectedHelp.includes(opt.id);
            return (
              <button
                key={opt.id}
                onClick={() => toggleHelp(opt.id)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 rounded-full border text-sm font-medium transition-all w-full',
                  selected ? 'border-[#1AB269] bg-[#edfaf3] text-[#1AB269]' : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                )}
              >
                {selected && <Check className="w-3.5 h-3.5 shrink-0" />}
                <span>{opt.label}</span>
              </button>
            );
          })}
        </div>
        <div className="flex justify-between">
          <Button variant="outline" onClick={() => setStep(0)} className="rounded-xl border-gray-200 px-6 h-11">Back</Button>
          <Button onClick={() => setStep(2)} className="rounded-xl bg-[#1AB269] hover:bg-[#158f56] text-white px-8 h-11">Continue</Button>
        </div>
      </div>
    </div>
  );

  // ── Step 2: Connect platforms ─────────────────────────────────────────────
  const renderStep2 = () => (
    <div className="flex flex-col items-center justify-center h-full px-10 py-8">
      <div className="w-full max-w-3xl">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Connect your platforms</h1>
        <p className="text-gray-500 mb-6">WeNext works best when connected to your business tools. You can always add more later.</p>

        <div className="grid grid-cols-2 gap-3 mb-8">
          {PLATFORMS.map(platform => {
            const connected = connectedPlatforms.has(platform.id);
            const Icon = platform.icon;
            return (
              <div key={platform.id} className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 bg-white">
                <Icon />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm">{platform.name}</div>
                  {platform.recommended && <div className="text-xs text-[#1AB269] font-medium">Recommended</div>}
                </div>
                <Button
                  size="sm"
                  variant={connected ? 'default' : 'outline'}
                  onClick={() => setOpenDialog(platform.id)}
                  className={cn(
                    'rounded-lg text-xs px-3 h-8 shrink-0',
                    connected ? 'bg-green-500 hover:bg-green-600 text-white border-0' : 'border-gray-300 text-gray-700'
                  )}
                >
                  {connected ? <><Check className="w-3 h-3 mr-1" />Connected</> : 'Connect'}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="flex justify-between items-center">
          <Button variant="outline" onClick={() => setStep(1)} className="rounded-xl border-gray-200 px-6 h-11">Back</Button>
          <div className="flex gap-3">
            <Button variant="ghost" onClick={saveAndComplete} disabled={saving} className="rounded-xl text-gray-600 h-11 px-6">Skip for now</Button>
            <Button onClick={saveAndComplete} disabled={saving} className="rounded-xl bg-[#1AB269] hover:bg-[#158f56] text-white px-8 h-11">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Get started
            </Button>
          </div>
        </div>
      </div>

      {/* Social OAuth dialogs */}
      <WhatsAppConnectDialog open={openDialog === 'whatsapp'} onClose={() => setOpenDialog(null)} onConnected={() => markConnected('whatsapp')} />
      <InstagramConnectDialog open={openDialog === 'instagram'} onClose={() => setOpenDialog(null)} onConnected={() => markConnected('instagram')} />
      <FacebookConnectDialog open={openDialog === 'facebook'} onClose={() => setOpenDialog(null)} onConnected={() => markConnected('facebook')} />
      <LinkedInConnectDialog open={openDialog === 'linkedin'} onClose={() => setOpenDialog(null)} onConnected={() => markConnected('linkedin')} />

      {/* Commerce credential dialogs */}
      <ShopifyDialog open={openDialog === 'shopify'} onClose={() => setOpenDialog(null)} onConnected={() => markConnected('shopify')} />
      <WooCommerceDialog open={openDialog === 'woocommerce'} onClose={() => setOpenDialog(null)} onConnected={() => markConnected('woocommerce')} />
      <RazorpayDialog open={openDialog === 'razorpay'} onClose={() => setOpenDialog(null)} onConnected={() => markConnected('razorpay')} />
      <PetPoojaDialog open={openDialog === 'petpooja'} onClose={() => setOpenDialog(null)} onConnected={() => markConnected('petpooja')} />
    </div>
  );

  return (
    <div className="w-full h-full bg-[#f7f7f5] flex flex-col">
      <div className="flex items-center justify-center gap-2 pt-6">
        {[0, 1, 2].map(i => (
          <div key={i} className={cn('rounded-full transition-all', i === step ? 'w-6 h-2 bg-[#1AB269]' : i < step ? 'w-2 h-2 bg-[#1AB269]/50' : 'w-2 h-2 bg-gray-200')} />
        ))}
      </div>
      <div className="flex-1">
        {step === 0 && renderStep0()}
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
      </div>
    </div>
  );
}
